import { translatedDocumentPath } from '../../shared/documentContent';
import { resolveSitePageRedirect, isDocumentPath } from "../../shared/sitePagePolicy";
import { buildLanguageUrl, getLanguageBaseUrl, stripLanguagePrefix } from "../../shared/localeRouting";
/**
 * Cấu hình URL chuyển hướng tùy chỉnh cho từng ngôn ngữ.
 *
 * Sơ đồ URL (src/shared/localeRouting.js): vi = root, ja = ja.qrcode-gen.com,
 * các ngôn ngữ còn lại = https://qrcode-gen.com/<lang>. Ở đây chỉ khai báo
 * pathMappings (slug khác nhau giữa các ngôn ngữ); baseUrl luôn lấy từ localeRouting.
 */

export const languageRedirects = {
  // Tiếng Việt - root domain, có path mapping slug tiếng Việt ↔ tiếng Anh
  'vi': {
    baseUrl: getLanguageBaseUrl('vi'),
    pathMappings: {
      // Các mapping đường dẫn cơ bản.
      // LƯU Ý: mỗi path tiếng Việt chỉ được có ĐÚNG MỘT key EN — getReverseMapping()
      // lấy match đầu tiên, hai key cùng value sẽ làm hreflang/canonical trỏ sai URL
      // (đã xảy ra với '/privacy' + '/privacy-policy' cùng trỏ '/chinh-sach-bao-mat').
      '/term-of-use': '/dieu-khoan-su-dung',
      '/privacy-policy': '/chinh-sach-bao-mat',
      
      // Các mapping cho hướng dẫn (guides)
      '/service-management-guide': '/huong-dan-quan-ly-dich-vu',
      '/event-registration-guide': '/huong-dan-dang-ky-su-kien',
      '/guide-for-saving-wifi-password': '/huong-dan-luu-pass-wifi',
      '/guide-for-creating-quizzes': '/huong-dan-tao-trac-nghiem',
      '/guide-for-creating-vcard': '/huong-dan-tao-vcard',
      '/guide-for-creating-pdf': '/huong-dan-tao-pdf',
      '/qr-code-menu-guide': '/huong-dan-menu-qr-code',
      '/asset-management-guide': '/huong-dan-quan-ly-tai-san',
      
      // Thêm các mapping còn thiếu
      '/huong-dan-tao-gift-code': '/huong-dan-tao-gift-code', // Giữ nguyên vì đã là tiếng Việt
      '/blog': '/blog' // Blog giữ nguyên
    }
  }
  // Các ngôn ngữ khác dùng quy tắc mặc định của localeRouting:
  //   ja → https://ja.qrcode-gen.com, còn lại → https://qrcode-gen.com/<lang>
};

/**
 * Hàm lấy URL redirect cho một ngôn ngữ cụ thể, kèm theo xử lý path mapping
 * @param {string} langCode - Mã ngôn ngữ (e.g., 'en', 'ja', 'vi')
 * @param {string} currentPath - Đường dẫn hiện tại (không bao gồm domain)
 * @param {string} currentSearch - Query string hiện tại
 * @returns {string|null} URL chuyển hướng hoàn chỉnh hoặc null nếu không được cấu hình
 */
export const getRedirectUrl = (langCode, currentPath, currentSearch) => {
  // currentPath có thể còn mang tiền tố ngôn ngữ hiện tại (window.location.pathname) → bỏ trước.
  currentPath = stripLanguagePrefix(currentPath || '/');
  const search = currentSearch || '';
  const policyTarget = resolveSitePageRedirect(currentPath, langCode);
  if (policyTarget?.startsWith('https://')) return policyTarget + search;
  currentPath = policyTarget || currentPath;
  // Article slugs differ between translations; use the target guide index until a matching slug is resolved.
  if (isDocumentPath(currentPath) && currentPath !== '/document') currentPath = translatedDocumentPath(currentPath, langCode);
  const redirectConfig = languageRedirects[langCode];

  let targetPath = currentPath;
  if (redirectConfig && typeof redirectConfig === 'object' && redirectConfig.pathMappings?.[currentPath]) {
    targetPath = redirectConfig.pathMappings[currentPath];
  }
  return buildLanguageUrl(langCode, targetPath, search);
};

/**
 * Hàm lấy reverse mapping từ URL tiếng Việt sang URL tiếng Anh
 * @param {string} vietnamesePath - Đường dẫn tiếng Việt
 * @returns {string} Đường dẫn tiếng Anh tương ứng hoặc đường dẫn gốc nếu không tìm thấy
 */
export const getReverseMapping = (vietnamesePath) => {
  const viConfig = languageRedirects['vi'];
  
  if (!viConfig || !viConfig.pathMappings) {
    return vietnamesePath;
  }
  
  // Tìm key (đường dẫn tiếng Anh) từ value (đường dẫn tiếng Việt)
  for (const [englishPath, viPath] of Object.entries(viConfig.pathMappings)) {
    if (viPath === vietnamesePath) {
      return englishPath;
    }
  }
  
  return vietnamesePath;
};
